import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Calendar, Clock, User } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
const groupPostsByCategory = (posts: any[]) => {
  const groups: Record<string, any[]> = {};
  posts.forEach(post => {
    const category = post.categories?.[0]?.name || "General Insights";
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(post);
  });
  return groups;
};
function PerspectivesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="rounded-lg bg-card border border-border animate-pulse h-[420px]" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <p className="text-muted-foreground text-base">No articles to display at this time.</p>;
  }
  const postsByCategory = groupPostsByCategory(posts);
  const categories = Object.keys(postsByCategory).sort();
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    });
  };
  const calculateReadTime = (content: string) => {
    const words = content.replace(/<[^>]*>?/gm, '').split(/\s+/).length;
    return Math.ceil(words / 200);
  };
  return <div className="flex flex-col gap-16">
      {categories.map((category, catIdx) => <div key={category} data-index={catIdx} className="flex flex-col gap-8">
          <h2 className="font-default font-bold text-2xl md:text-3xl text-foreground border-b border-border pb-4">
            {category}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {postsByCategory[category].map(post => {
          const postUrl = post.link || "#";
          const plainExcerpt = post.excerpt?.replace(/<[^>]*>?/gm, '') || "";
          const readTime = calculateReadTime(post.content || "");
          return <div key={post.id} className="flex flex-col">
                  <Link to={postUrl} className="group flex flex-col rounded-lg bg-card border border-border overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-secondary h-full">
                    <p className="px-7 pt-7 pb-0 text-sm text-muted-foreground italic">
                      Expert analysis on {post.title?.toLowerCase().slice(0, 40)}...
                    </p>
                    <div className="relative aspect-[16/10] overflow-hidden bg-muted mt-4">
                      <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/blog_abstract_shapes.webp"} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    </div>
                    <div className="flex flex-col gap-3 p-7">
                      <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground uppercase">
                        <span className="flex items-center gap-1"><SafeHydrate><Calendar className="size-3" /></SafeHydrate> {formatDate(post.date)}</span>
                        <span className="flex items-center gap-1"><Clock className="size-3" /> {readTime} min read</span>
                      </div>
                      <h3 className="font-default text-xl font-semibold text-foreground group-hover:text-secondary transition-colors">
                        {post.title}
                      </h3>
                      <div className="text-muted-foreground text-base leading-relaxed" dangerouslySetInnerHTML={{
                  __html: post.excerpt || ""
                }} />
                      <div className="mt-auto pt-4 flex items-center gap-2 font-mono text-xs uppercase text-muted-foreground/60">
                        <User className="size-3" />
                        <span>By {post.author?.name || "HotelRevBaBa Team"}</span>
                      </div>
                    </div>
                  </Link>
                </div>;
        })}
          </div>
        </div>)}
    </div>;
}
export default function Blog() {
  return <section data-section-id="16507" id="blog-feed" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-[1320px] px-5 md:px-10">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Home</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbPage>Insights</BreadcrumbPage>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <h1 className="text-2xl md:text-3xl mb-6">Hospitality Revenue Insights & Industry News</h1>
            <h2 className="font-default font-bold text-foreground text-[clamp(2.125rem,3.5vw+0.5rem,3.75rem)] leading-tight">
              Hotel Revenue Insights & Hospitality Industry Tips
            </h2>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Stay ahead of the competition with data-driven strategies, revenue management best practices, and the latest hospitality technology trends curated by industry experts.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-9">
            <PostsProvider wp_query={{
            post_type: "post",
            per_page: 10,
            order: "desc",
            orderby: "date",
            status: ["publish"]
          }}>
              <PerspectivesConsumer />
            </PostsProvider>
          </div>
          <aside className="lg:col-span-3">
            <div className="sticky top-24 rounded-lg border border-border bg-card p-6">
              <h3 className="font-semibold text-lg mb-4">Explore Next</h3>
              <nav className="flex flex-col gap-3">
                <Link to="/ResourcesPage" className="text-muted-foreground hover:text-secondary">Resources</Link>
              </nav>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}